import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCookie } from 'react-use';

import { useUserTrialDetails } from '@revelio/auth';
import { useTourStore } from '@revelio/core';
import { SelectionCategories } from '@revelio/iso-utility';

import { useTabContentConfig } from '../../components/PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../components/PageConfigProvider/types';
import { useAiSearchContextSafe } from '../contexts/AiSearchContext';
import { useFilterContext } from '../contexts/FilterProvider';
import { useAllSelectionLists } from '../hooks/useAllSelectionLists';
import { FilterId } from '../types/filters';
import { defaultTrialAiSearchState } from './defaultTrialAiSearchState';
import { useApplyAiSearchFilters } from './useApplyAiSearchFilters';

const CUSTOM_ROLE_GRANULARITY_FILTERS = [
  SelectionCategories.ROLE_K7,
  SelectionCategories.ROLE_K150,
  SelectionCategories.ROLE_K1500,
  SelectionCategories.ROLE_K15000,
];

/**
 * Applies the AI Search demo defaults for trial users on the
 * CustomDash Talent Discovery page when there are no URL filters.
 */
export const useTrialAiSearchDefaults = () => {
  const { revelioPageId, hasFilter } = useTabContentConfig();
  const [searchParams] = useSearchParams();

  const selectionLists = useAllSelectionLists();
  const [aiWriterDone, updateAiWriterDone] = useCookie('ai-writer');

  const aiSearchContext = useAiSearchContextSafe();

  const {
    setDefaultAiSearchSet = () => undefined,
    setAiSearchState = () => undefined,
    setAiGeneratedFilterIds = () => undefined,
    defaultAiSearchSet = false,
    aiGeneratedFilterIds = [],
  } = aiSearchContext || {};

  const { getFilterData, activeFilterMap, hadInitialUrlFilters } =
    useFilterContext();
  const { applyAiSearchFilters } = useApplyAiSearchFilters();

  const { isTrialUser, isSelfSignupUser } = useUserTrialDetails<boolean>({
    initialRenderValue: false,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const userSubmittedFilters = Array.from(activeFilterMap.entries()).filter(
      ([, filter]) => Boolean(filter?.isUserSubmitted)
    );
    const hasUserSubmittedPageFilters = userSubmittedFilters.some(
      ([filterId]) => hasFilter(filterId)
    );
    const isTourActive = useTourStore.getState().isTour;

    // Check that roles have been loaded into the filter map for lookup
    const roleTreeReady = (() => {
      try {
        const data = getFilterData(FilterId.ROLE);
        return Array.isArray(data) && data.length > 0;
      } catch {
        return false;
      }
    })();

    const hasRoleSelectionLists = CUSTOM_ROLE_GRANULARITY_FILTERS.every(
      (filterId) => selectionLists?.some((list) => list.id === filterId)
    );
    const shouldRunDemo =
      revelioPageId === RevelioPageId.TALENT_DISCOVERY &&
      (isTrialUser || isSelfSignupUser) &&
      !isTourActive &&
      !defaultAiSearchSet &&
      aiGeneratedFilterIds.length === 0 &&
      !hasUserSubmittedPageFilters &&
      Boolean(selectionLists) &&
      hasRoleSelectionLists &&
      roleTreeReady &&
      !hadInitialUrlFilters;

    if (!shouldRunDemo) {
      return;
    }

    // Clear the ai-writer cookie so typing animation runs for this demo if it
    // was previously set (e.g., from another page)
    try {
      if (aiWriterDone !== null) {
        updateAiWriterDone('', { expires: new Date(0) });
      }
    } catch (e) {
      console.error(e);
    }

    setDefaultAiSearchSet(true);
    setAiSearchState(defaultTrialAiSearchState);

    const response = defaultTrialAiSearchState.response;
    if (!response || !('relevantPromptText' in response)) return;

    applyAiSearchFilters(response)
      .then(({ adaptedFilters }) => {
        setAiGeneratedFilterIds(adaptedFilters.map((f) => f.id));
      })
      .catch((err) => {
        console.warn('[AI Search] Failed to apply default trial filters', err);
      });
  }, [
    revelioPageId,
    searchParams,
    isTrialUser,
    isSelfSignupUser,
    selectionLists,
    defaultAiSearchSet,
    aiGeneratedFilterIds,
    activeFilterMap,
    aiWriterDone,
    updateAiWriterDone,
    setDefaultAiSearchSet,
    setAiSearchState,
    setAiGeneratedFilterIds,
    applyAiSearchFilters,
    getFilterData,
    hadInitialUrlFilters,
    hasFilter,
  ]);
};
